import {Component, OnInit} from '@angular/core';
import {transition, trigger, useAnimation} from '@angular/animations';
import {FormBuilder, FormGroup} from '@angular/forms';
import {fadeInAnimation} from '../../../../shared/animation/common.animation';
import {faCopy} from '@fortawesome/pro-duotone-svg-icons';
import {faAngleDown, faAngleLeft} from '@fortawesome/free-solid-svg-icons';
import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import {ActivityTypeEnum} from '../../models/activity-type.enum';
import {ElectronicInvoiceLine} from '../../models/electronic-invoice-line.model';
import {ElectronicInvoiceToGenerateService} from '../../services/electronic-invoice-to-generate.service';

interface PurchaseOrderOption {
  /** Unique key: same PO can appear for several clients (BR7). */
  value: string;
  purchaseOrderNumber: string;
  clientName: string;
  label: string;
}

@Component({
  selector: 'electronic-invoice-to-generate',
  templateUrl: './electronic-invoice-to-generate.component.html',
  styleUrls: ['./electronic-invoice-to-generate.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        useAnimation(fadeInAnimation)
      ])
    ]),
  ],
})
export class ElectronicInvoiceToGenerateComponent implements OnInit {
  protected readonly faCopy = faCopy;
  protected readonly faAngleDown = faAngleDown;
  protected readonly faAngleLeft = faAngleLeft;
  protected readonly ActivityTypeEnum = ActivityTypeEnum;

  allLines: ElectronicInvoiceLine[] = [];
  displayedLines: ElectronicInvoiceLine[] = [];
  purchaseOrderOptions: PurchaseOrderOption[] = [];
  selectedPurchaseOrderValue: string | null = null;
  loading = true;
  advancedOpen = false;
  quickSearch = '';
  advancedForm: FormGroup;
  typeOptions = [
    ActivityTypeEnum.ACTIVITY_SHEET,
    ActivityTypeEnum.FEE,
    ActivityTypeEnum.ON_CALL,
    ActivityTypeEnum.DUE_PAYMENT,
    ActivityTypeEnum.REGULATION,
  ];
  private appliedAdvanced: {
    activityId?: string;
    clientName?: string;
    periodKey?: string;
    types?: ActivityTypeEnum[];
  } | null = null;

  constructor(
    private electronicInvoiceService: ElectronicInvoiceToGenerateService,
    private fb: FormBuilder,
    private toastrService: ToastrService,
    private translateService: TranslateService,
  ) {
    this.advancedForm = this.fb.group({
      activityId: [null],
      clientName: [null],
      period: [null],
      types: [[]],
    });
  }

  get hasSelectedPurchaseOrder(): boolean {
    return !!this.selectedPurchaseOrderValue;
  }

  ngOnInit(): void {
    this.electronicInvoiceService.loadLines().subscribe({
      next: lines => {
        this.allLines = lines;
        this.purchaseOrderOptions = this.buildPurchaseOrderOptions(lines);
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastrService.error(this.translateService.instant('global.ts.error'));
      }
    });
  }

  onPurchaseOrderChange(value: string | null): void {
    this.selectedPurchaseOrderValue = value || null;
    this.applyFilters();
  }

  onQuickSearchChange(value: string): void {
    this.quickSearch = value || '';
    if (!this.advancedOpen) {
      this.applyFilters();
    }
  }

  toggleAdvanced(): void {
    this.advancedOpen = !this.advancedOpen;
    if (!this.advancedOpen) {
      this.advancedForm.reset({activityId: null, clientName: null, period: null, types: []});
      this.appliedAdvanced = null;
      this.applyFilters();
    } else {
      this.quickSearch = '';
      this.applyFilters();
    }
  }

  clearAdvancedSearchPeriod(): void {
    this.advancedForm.get('period')?.reset();
    this.advancedForm.get('period')?.markAsDirty();
  }

  searchAdvanced(): void {
    if (this.advancedForm.pristine) {
      return;
    }
    const raw = this.advancedForm.value;
    const period: Date | null = raw.period || null;
    const periodKey = period
      ? `${period.getFullYear()}-${`${period.getMonth() + 1}`.padStart(2, '0')}`
      : undefined;
    this.appliedAdvanced = {
      activityId: (raw.activityId || '').trim() || undefined,
      clientName: (raw.clientName || '').trim() || undefined,
      periodKey,
      types: (raw.types || []).length ? raw.types : undefined,
    };
    this.applyFilters();
    this.advancedForm.markAsPristine();
  }

  copyValue(value: string): void {
    if (value == null) {
      return;
    }
    navigator.clipboard?.writeText(String(value));
  }

  typeLabel(type: ActivityTypeEnum): string {
    return this.translateService.instant(`to_generate.electronic.types.${type}`);
  }

  paymentDelayLabel(days?: number): string {
    if (days == null || isNaN(Number(days))) {
      return '';
    }
    return `${days}${this.translateService.instant('to_generate.electronic.payment_delay_unit')}`;
  }

  private buildPurchaseOrderOptions(lines: ElectronicInvoiceLine[]): PurchaseOrderOption[] {
    const seen = new Set<string>();
    const options: PurchaseOrderOption[] = [];
    for (const line of lines) {
      const purchaseOrderNumber = (line.purchaseOrderNumber || '').trim();
      if (!purchaseOrderNumber) {
        continue;
      }
      const clientName = (line.clientName || '').trim();
      const value = this.toPurchaseOrderOptionValue(purchaseOrderNumber, clientName);
      if (seen.has(value)) {
        continue;
      }
      seen.add(value);
      options.push({
        value,
        purchaseOrderNumber,
        clientName,
        label: `${purchaseOrderNumber} - ${clientName}`,
      });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label, undefined, {sensitivity: 'base'}));
  }

  private toPurchaseOrderOptionValue(purchaseOrderNumber: string, clientName: string): string {
    return `${purchaseOrderNumber}\u0000${clientName}`;
  }

  private selectedPurchaseOrderOption(): PurchaseOrderOption | null {
    if (!this.selectedPurchaseOrderValue) {
      return null;
    }
    return this.purchaseOrderOptions.find(option => option.value === this.selectedPurchaseOrderValue) || null;
  }

  private applyFilters(): void {
    const selected = this.selectedPurchaseOrderOption();
    if (!selected) {
      this.displayedLines = [];
      return;
    }
    let lines = this.allLines.filter(line =>
      (line.purchaseOrderNumber || '').trim() === selected.purchaseOrderNumber
      && (line.clientName || '').trim() === selected.clientName
    );
    if (this.advancedOpen && this.appliedAdvanced) {
      const f = this.appliedAdvanced;
      lines = lines.filter(line => {
        if (f.activityId && !line.activityId.toLowerCase().includes(f.activityId.toLowerCase())) {
          return false;
        }
        if (f.clientName && !String(line.clientName || '').toLowerCase().includes(f.clientName.toLowerCase())) {
          return false;
        }
        if (f.periodKey && line.periodKey !== f.periodKey) {
          return false;
        }
        if (f.types?.length && !f.types.includes(line.type)) {
          return false;
        }
        return true;
      });
    } else if (!this.advancedOpen && this.quickSearch.trim().length > 0) {
      const q = this.quickSearch.trim().toLowerCase();
      lines = lines.filter(line =>
        line.activityId.toLowerCase().includes(q)
        || String(line.clientName || '').toLowerCase().includes(q)
      );
    }
    this.displayedLines = lines;
  }
}
